import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BlogSection = () => {
  const [headerAnimated, setHeaderAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHeaderAnimated(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const blogPosts = [
    {
      id: 1,
      title: "The Future of Web Development: Trends to Watch in 2024",
      excerpt: "Explore the latest trends shaping the web development landscape, from AI integration to progressive web apps and the rise of serverless architecture.",
      author: "Rajesh Kumar",
      authorAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      publishDate: "March 15, 2024",
      readTime: "8 min read",
      category: "Web Development",
      image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      tags: ["React", "AI", "PWA", "Serverless"]
    },
    {
      id: 2,
      title: "Mobile App Security: Best Practices for 2024",
      excerpt: "Learn essential security measures to protect your mobile applications from threats, including data encryption, secure authentication, and API security.",
      author: "Priya Sharma",
      authorAvatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      publishDate: "March 10, 2024",
      readTime: "6 min read",
      category: "Mobile Development",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=fit&w=800&q=80",
      tags: ["Security", "Mobile", "Authentication", "Encryption"]
    },
    {
      id: 3,
      title: "Cloud Migration Strategies: A Complete Guide",
      excerpt: "Discover proven strategies for successful cloud migration, including planning, execution, and optimization techniques for maximum ROI and minimal downtime.",
      author: "Amit Patel",
      authorAvatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      publishDate: "March 5, 2024",
      readTime: "10 min read",
      category: "Cloud Solutions",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      tags: ["AWS", "Migration", "Cloud", "DevOps"]
    }
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Web Development':
        return 'bg-blue-600 text-white';
      case 'Mobile Development':
        return 'bg-green-600 text-white';
      case 'Cloud Solutions':
        return 'bg-purple-600 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // UPDATED: Animation variants to prevent horizontal overflow space
  const cardVariants = {
    hidden: (i) => ({
      opacity: 0,
      y: 40, // Side thi badle niche thi upar ave to space no issue nai rahe
      x: i === 0 ? -20 : i === 2 ? 20 : 0, // Bahu moti value nai rakhvani (like 100)
      scale: 0.95
    }),
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    },
  };

  const headerContainerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    },
  };

  const headerWordVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.8 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
  };

  return (
    // ADDED: overflow-x-hidden to the main section
    <section className="py-16 sm:py-8 bg-gray-50 dark:bg-gray-900 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <motion.div
          className="text-center mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={headerContainerVariants}
        >
          <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
            {`Latest from Our Blog`.split(" ").map((word, i) => (
              <motion.span
                key={i}
                className="inline-block mr-2"
                variants={headerWordVariants}
              >
                {word}
              </motion.span>
            ))}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            {`Stay updated with the latest insights, trends, and best practices in technology and software development.`.split(" ").map((word, i) => (
              <motion.span
                key={i}
                className="inline-block mr-1"
                variants={headerWordVariants}
              >
                {word}
              </motion.span>
            ))}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {blogPosts.map((post, index) => (
            <motion.article
              key={post.id}
              className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-xl border border-gray-100 dark:border-gray-700 cursor-pointer"
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              whileHover={{ scale: 1.03, rotate: 0.5, transition: { duration: 0.3 } }}
            >
              <Link to="/blog" aria-label={`Read more about ${post.title}`}>
                <div className="relative overflow-hidden h-52">
                  <Image
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-10 transition-all duration-300"></div>

                  <div className="absolute top-4 left-4">
                    <span className={`px-4 py-1 text-sm font-bold rounded-full shadow ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                  </div>

                  <div className="absolute top-4 right-4">
                    <span className="bg-white dark:bg-gray-900 bg-opacity-90 dark:bg-opacity-80 text-gray-800 dark:text-gray-200 px-3 py-1 text-sm font-medium rounded-lg backdrop-blur-sm">
                      <Icon name="Clock" size={14} className="inline mr-1" /> {post.readTime}
                    </span>
                  </div>
                </div>

                <div className="p-6 sm:p-8">
                  <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-3 leading-snug hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300">
                    {post.title}
                  </h3>

                  <p className="text-gray-500 dark:text-gray-400 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {post.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400 text-xs font-semibold rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center space-x-3">
                      <Image
                        src={post.authorAvatar}
                        alt={post.author}
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-blue-500 dark:ring-blue-400"
                      />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {post.author}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(post.publishDate)}
                        </p>
                      </div>
                    </div>

                    <span
                      className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 group-hover:translate-x-1 transition-all duration-300"
                    >
                      Read More
                      <Icon name="ArrowRight" size={16} className="ml-1" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
            Want to read more insights and tutorials? Check out our complete blog.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center px-10 py-4 bg-[#1e40af] text-white font-bold text-lg rounded-full shadow-lg shadow-blue-500/50 hover:bg-blue-700 transition-all duration-300 transform hover:scale-105"
          >
            View All Posts
            <Icon name="BookOpen" size={20} className="ml-2" />
          </Link>
        </motion.div>

        <motion.div
          className="mt-12"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-gradient-to-br from-[#1e50af] to-indigo-700 rounded-3xl p-10 lg:p-16 text-center shadow-2xl shadow-indigo-500/50">
            <div className="max-w-2xl mx-auto">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 1, ease: "easeInOut", repeat: Infinity, repeatDelay: 5 }}
              >
                <Icon name="Mail" size={48} className="text-white mx-auto mb-6 drop-shadow" />
              </motion.div>
              <h3 className="text-3xl lg:text-4xl font-extrabold text-white mb-4 drop-shadow-md">
                Stay Updated with Our Newsletter
              </h3>
              <p className="text-blue-100 mb-10 text-lg">
                Get the latest tech insights, tutorials, and industry news delivered straight to your inbox.
              </p>

              <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-5 py-3 rounded-full border-none focus:outline-none focus:ring-4 focus:ring-white/50 text-gray-800 placeholder-gray-500 transition-all duration-300"
                  required
                />
                <motion.button
                  type="submit"
                  className="px-6 py-3 bg-white text-blue-600 font-bold rounded-full hover:bg-gray-100 transition-colors duration-300 shadow-lg whitespace-nowrap"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Subscribe Now
                </motion.button>
              </form>

              <p className="text-blue-200 text-sm mt-4">
                No spam, unsubscribe at any time. We respect your privacy.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;